import { apiClient } from '@/lib/apiClient';

import type {
  ConfirmarFotoRequest,
  PresignedUploadRequest,
  PresignedUploadResponse,
} from '../common/types';
import { assertAllowedPhotoFile, uploadToS3 } from '../common/fotos';

import type { SedimentacionFh, SedimentacionFhCreate, SedimentacionFhUpdate } from './types';

const base = (uidProtocolo: string) =>
  `/protocolos/${uidProtocolo}/analisis-sedimentacion-fh`;

export const sedimentacionFhApi = {
  // ─── CRUD ───────────────────────────────────────────────

  async create(
    uidProtocolo: string,
    data: SedimentacionFhCreate,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.post<SedimentacionFh>(base(uidProtocolo), data, { signal });
    return response.data;
  },

  async listByProtocolo(uidProtocolo: string, signal?: AbortSignal): Promise<SedimentacionFh[]> {
    const response = await apiClient.get<SedimentacionFh[]>(base(uidProtocolo), { signal });
    return response.data;
  },

  async getById(
    uidProtocolo: string,
    uidAnalisis: string,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.get<SedimentacionFh>(
      `${base(uidProtocolo)}/${uidAnalisis}`,
      { signal }
    );
    return response.data;
  },

  async update(
    uidProtocolo: string,
    uidAnalisis: string,
    data: SedimentacionFhUpdate,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.patch<SedimentacionFh>(
      `${base(uidProtocolo)}/${uidAnalisis}`,
      data,
      { signal }
    );
    return response.data;
  },

  async remove(uidProtocolo: string, uidAnalisis: string, signal?: AbortSignal): Promise<void> {
    await apiClient.delete(`${base(uidProtocolo)}/${uidAnalisis}`, { signal });
  },

  // ─── Fotos ──────────────────────────────────────────────

  async requestPresignedUpload(
    uidProtocolo: string,
    uidAnalisis: string,
    body: PresignedUploadRequest,
    signal?: AbortSignal
  ): Promise<PresignedUploadResponse> {
    const response = await apiClient.post<PresignedUploadResponse>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos`,
      body,
      { signal }
    );
    return response.data;
  },

  async confirmarFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    body: ConfirmarFotoRequest,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.post<SedimentacionFh>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/confirmar`,
      body,
      { signal }
    );
    return response.data;
  },

  async eliminarFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    uidFoto: string,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.delete<SedimentacionFh>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/${uidFoto}`,
      { signal }
    );
    return response.data;
  },

  async editarCaption(
    uidProtocolo: string,
    uidAnalisis: string,
    uidFoto: string,
    caption: string | null,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    const response = await apiClient.patch<SedimentacionFh>(
      `${base(uidProtocolo)}/${uidAnalisis}/fotos/${uidFoto}/caption`,
      { caption },
      { signal }
    );
    return response.data;
  },

  /**
   * Flujo completo de upload de una foto:
   * 1. Solicitar presigned URL.
   * 2. Subir el archivo a S3 (fetch directo, sin Axios).
   * 3. Confirmar en el backend.
   *
   * Valida el content_type del File en cliente antes de pedir la presigned URL.
   */
  async uploadFoto(
    uidProtocolo: string,
    uidAnalisis: string,
    file: File,
    caption: string | null = null,
    signal?: AbortSignal
  ): Promise<SedimentacionFh> {
    assertAllowedPhotoFile(file);

    const presigned = await this.requestPresignedUpload(
      uidProtocolo,
      uidAnalisis,
      { content_type: file.type, caption },
      signal
    );

    await uploadToS3(presigned.upload_url, file);

    return await this.confirmarFoto(
      uidProtocolo,
      uidAnalisis,
      { uid_foto: presigned.uid_foto, content_type: file.type, caption: presigned.caption },
      signal
    );
  },
};
